import { useState } from "react";
import { $ } from "../lib/format";
import { ACCENT, ACCENT2, BORDER, CARD, DARK, DIM, MID, RED } from "../theme";
import { welcomeLine } from "../homeText";

// Top of the Home screen on phones and tablets: a greeting, the period, three headline numbers and
// "Needs your attention". All numbers come from the dashboard; nothing is worked out here.
const SANS = "'DM Sans',sans-serif";

function Change({ pct, label }) {
  if (pct == null) return null;
  return (
    <div style={{ fontFamily: SANS, fontSize: 14, fontWeight: 500, color: pct >= 0 ? ACCENT2 : RED, marginTop: 4 }}>
      <span aria-hidden="true">{pct >= 0 ? "▲" : "▼"}</span> {Math.abs(pct)}%
      <span style={{ fontWeight: 400, color: DIM }}> {label}</span>
    </div>
  );
}

const ROWS_SHOWN = 4;
const tile = { textAlign: "left", cursor: "pointer", font: "inherit", background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "14px 14px", minWidth: 0, minHeight: 44, boxShadow: "0 1px 4px rgba(44,36,22,0.04)" };

function HomeWelcome({ hour, name, period, profit, profitIsNet, profitPct, revenue, revenuePct, comparisonLabel, margin, marginNote, expenseView, onExpenseView, onOpen, rows, onRow, activeRow }) {
  const profitColor = profit >= 0 ? ACCENT2 : RED;
  const [showAll, setShowAll] = useState(false);
  const shownRows = showAll ? rows : rows.slice(0, ROWS_SHOWN);
  const hidden = rows.length - ROWS_SHOWN;
  return (
    <section aria-label="Home" style={{ marginBottom: 20 }}>
      <h1 style={{ fontFamily: "'Lora',serif", fontSize: 24, fontWeight: 600, color: DARK, letterSpacing: "-0.02em", margin: 0, overflowWrap: "anywhere" }}>{welcomeLine(hour, name)}</h1>
      <p style={{ fontFamily: SANS, fontSize: 15, color: MID, margin: "6px 0 16px", lineHeight: 1.4 }}>{period}</p>

      <div style={{ ...tile, cursor: "default", borderTop: `4px solid ${profitColor}`, padding: 0, display: "block" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "10px 14px 0" }}>
          <span style={{ fontFamily: SANS, fontSize: 14, fontWeight: 600, color: DIM }}>{profitIsNet ? "Net profit" : "Gross profit"}</span>
          <div role="group" aria-label="Profit view" style={{ display: "flex", border: `1px solid ${BORDER}`, borderRadius: 6, overflow: "hidden" }}>
            {[["job", "Gross"], ["fixed", "Net"]].map(([k, l], i) => (
              <button key={k} type="button" aria-pressed={expenseView === k} onClick={() => onExpenseView(k)}
                style={{ cursor: "pointer", minWidth: 56, minHeight: 44, fontSize: 14, fontWeight: 600, fontFamily: SANS, border: "none", borderRight: i === 0 ? `1px solid ${BORDER}` : "none", background: expenseView === k ? ACCENT2 : CARD, color: expenseView === k ? CARD : DIM }}>{l}</button>
            ))}
          </div>
        </div>
        <button type="button" aria-label={`${profitIsNet ? "Net profit" : "Gross profit"} ${$(profit)}. Open details`} onClick={() => onOpen("profit")}
          style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", font: "inherit", padding: "4px 14px 14px", minHeight: 44 }}>
          <div style={{ fontFamily: "'Lora',serif", fontSize: 36, fontWeight: 700, color: profitColor, letterSpacing: "-0.03em", lineHeight: 1.1 }}>{$(profit)}</div>
          <Change pct={profitPct} label={comparisonLabel} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginTop: 12 }}>
        <button type="button" aria-label={`Revenue ${$(revenue)}. Open details`} onClick={() => onOpen("revenue")} style={{ ...tile, borderTop: `4px solid ${ACCENT}` }}>
          <div style={{ fontFamily: SANS, fontSize: 14, fontWeight: 600, color: DIM }}>Revenue</div>
          <div style={{ fontFamily: "'Lora',serif", fontSize: 24, fontWeight: 700, color: DARK, margin: "4px 0 0", overflowWrap: "anywhere" }}>{$(revenue)}</div>
          <Change pct={revenuePct} label={comparisonLabel} />
        </button>
        <button type="button" aria-label={`${profitIsNet ? "Net" : "Gross"} margin ${margin == null ? "not available yet" : margin + " percent"}. Open details`} onClick={() => onOpen("profit")} style={{ ...tile, borderTop: `4px solid ${profitColor}` }}>
          <div style={{ fontFamily: SANS, fontSize: 14, fontWeight: 600, color: DIM }}>{profitIsNet ? "Net margin" : "Gross margin"}</div>
          <div style={{ fontFamily: "'Lora',serif", fontSize: 24, fontWeight: 700, color: margin == null ? DIM : profitColor, margin: "4px 0 0" }}>{margin == null ? "None yet" : `${margin}%`}</div>
          <div style={{ fontFamily: SANS, fontSize: 14, color: DIM, marginTop: 4 }}>{marginNote}</div>
        </button>
      </div>

      <h2 style={{ fontFamily: "'Lora',serif", fontSize: 18, fontWeight: 600, color: DARK, margin: "22px 0 8px" }}>Needs your attention</h2>
      {rows.length === 0 ? (
        <div role="status" style={{ ...tile, cursor: "default", fontFamily: SANS, fontSize: 15, color: ACCENT2, borderLeft: `4px solid ${ACCENT2}`, display: "flex", alignItems: "center" }}>All caught up. Nothing needs your attention right now.</div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, border: `1px solid ${BORDER}`, borderRadius: 8, background: CARD, overflow: "hidden" }}>
          {shownRows.map((r, i) => (
            <li key={r.key} style={{ borderTop: i ? `1px solid ${BORDER}` : "none" }}>
              <button type="button" onClick={() => onRow(r.key)} aria-pressed={r.key === "expenses" ? undefined : activeRow === r.key}
                style={{ width: "100%", minHeight: 48, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, textAlign: "left", padding: "10px 14px", border: "none", background: activeRow === r.key ? "rgba(196,144,32,0.10)" : "none", cursor: "pointer", fontFamily: SANS, fontSize: 15, color: DARK }}>
                <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{r.text}</span>
                <span aria-hidden="true" style={{ color: DIM, fontSize: 18, flexShrink: 0 }}>›</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {rows.length > ROWS_SHOWN && (
        <button type="button" aria-expanded={showAll} onClick={() => setShowAll(v => !v)}
          style={{ marginTop: 8, minHeight: 44, padding: "0 4px", background: "none", border: "none", cursor: "pointer", fontFamily: SANS, fontSize: 15, fontWeight: 500, color: ACCENT }}>
          {showAll ? "Show fewer" : `Show ${hidden} more`}
        </button>
      )}
    </section>
  );
}

export { HomeWelcome };
